import { IScores, setScores } from '@/context/borrower';
import { selectWidgetId } from '@/context/display';
import { setAndSortLenders } from '@/context/lender';
import { store } from '@/context/store';
import { EJourneyEvent, emitJourneyEvent } from '@/journeyEvents';
import { getCommissionAmount } from '@/utils/getCommissionAmount';
import { isDemoMode } from '@/utils/setDemoMode';

import { postUrl } from '../fetch';
import { trackedServiceCall } from '../uniqueIDTransferHelper';

import {
    EPreApprovalVerdicts,
    IPreApprovalResponse,
} from './fetchPreapprovalVerdict.types';

export async function fetchPreapprovalVerdict(
    creditScoreTestDrive?: boolean,
    isTestDrive?: boolean
): Promise<IPreApprovalResponse> {
    // ***** Send the Request *****
    const url = '/api/submit/preApproval';

    const state = store.getState();
    const customerName = state.borrowerReducer.personalDetails.firstName;
    const applicationId =
        state.borrowerReducer.application.applicationId ||
        state.borrowerReducer.application.id;
    // Get Pre Approval parameters
    const lowestMonthlyRepayment =
        state.lenderReducer.lenders?.[0]?.monthlyRepaymentAmount; // TODO: filter to actually get the lowest one

    const sendLeadEmail = state.displayReducer.sendLeadEmail;

    let waterfallLenders: string[][] | undefined = undefined;
    if (state.displayReducer.waterfallLenders) {
        waterfallLenders = [];
        for (let i = 0; i < state.displayReducer.waterfallLenders.length; i++) {
            waterfallLenders.push(
                state.displayReducer.waterfallLenders[i].lenders
            );
        }
    }
    const isDemo = isDemoMode();

    // Get partner commission amount
    const commissionAmount = getCommissionAmount();

    // Instrumented: preapproval is where the Score service runs, and it is the
    // slowest thing in the journey — a long wait loses applicants without ever
    // returning an error, so the duration matters as much as the verdict.
    const response = await trackedServiceCall('preApproval', () =>
        postUrl<IPreApprovalResponse>(url, {
            monthlyRepayment: lowestMonthlyRepayment,
            applicationId,
            waterfallLenders,
            sendLeadEmail,
            commissionAmount,
            isDemoMode: isDemo,
            creditScoreTestDrive: creditScoreTestDrive,
            widgetId: selectWidgetId(state),
        })
    );

    const isCustomerIniatedFlow = state.borrowerReducer.application
        .DIFBrokerPhoneNumber
        ? false
        : true;
    if (
        response.verdict === EPreApprovalVerdicts.REFERRED &&
        isTestDrive &&
        !isCustomerIniatedFlow
    ) {
        const body = {
            mobilePhone: state.borrowerReducer.application.DIFBrokerPhoneNumber,
            message: `Your Customer ${customerName} with application id ${applicationId} is referred due to low credit score.`,
            partner: state.displayReducer.partner.id,
        };
        postUrl('/api/sendCustomMessage', body);
    }
    if (
        response.verdict === EPreApprovalVerdicts.APPROVED &&
        isTestDrive &&
        !isCustomerIniatedFlow
    ) {
        const body = {
            mobilePhone: state.borrowerReducer.application.DIFBrokerPhoneNumber,
            message: `Your Customer ${customerName} with application id ${applicationId} is pre approved.`,
            partner: state.displayReducer.partner.id,
        };
        postUrl('/api/sendCustomMessage', body);
    }

    // ***** Dispatch response to state *****
    const stateScores = state.borrowerReducer.scores;

    // response returns number | null but FE saves number | undefined
    const scores: IScores = {
        onePointOneScore: response.onePointOneScore
            ? response.onePointOneScore
            : undefined,
        comprehensiveScore: response.comprehensiveScore
            ? response.comprehensiveScore
            : undefined,
        creditScore: response.creditScore ? response.creditScore : undefined,
        creditScorePercentage: response.creditScorePercentage,
        eligibilityScorePercentage: response.eligibilityScorePercentage,
        accuracyScorePercentage: response.accuracyScorePercentage,
    };

    // set scores to state
    store.dispatch(
        setScores({
            ...stateScores, // other scores that aren't returned by this API
            ...scores,
        })
    );

    // Set approved lenders to state
    store.dispatch(setAndSortLenders(response.approvedLenders));

    /*
     * A credit file was pulled — whatever the verdict said about it.
     *
     * Gated on the API's explicit flag, never on a score. resultToReturn is
     * initialised with creditScore 850 and verdict APPROVED, so a partner
     * with no preapprovalFlow receives a response carrying a score no bureau
     * produced; reading creditScore here would announce a credit check that
     * never happened. Those partners still reach the finance options, so they
     * get LENDERS_PRESENTED and never this.
     */
    if (response.creditFileChecked === true) {
        emitJourneyEvent(EJourneyEvent.CREDIT_CHECKED);
    }

    return response;
}
